import type { DashboardMatrixPoint } from "@/shared/api/api-client";
import { formatCena, formatInteger } from "@/shared/utils/format";

import { categoricalTone, useChartTip, useChartWidth } from "./chart-kit";

/** A cor de cada entidade a partir de uma ordem ESTÁVEL de chaves (as
 *  cinco primeiras com cor própria, o resto em "outros"). */
export function tonesByKey(keys: string[]): (key: string) => string {
  const order = new Map(keys.map((key, index) => [key, categoricalTone(index)]));
  return key => order.get(key) ?? "cat-other";
}
import { EmptyState } from "./EmptyState";

/** As seis zonas do v1, de cima pra baixo e da esquerda pra direita, e a
 *  faixa de `y` de cada linha (a de baixo é a metade negativa inteira). */
const ZONES = [
  { key: "amplificar", tone: "positive-soft", row: 0, col: 0 },
  { key: "sustentar", tone: "positive", row: 0, col: 1 },
  { key: "reagir", tone: "warning-soft", row: 1, col: 0 },
  { key: "qualificar", tone: "accent-soft", row: 1, col: 1 },
  { key: "recalcular", tone: "negative-soft", row: 2, col: 0 },
  { key: "intervir", tone: "negative", row: 2, col: 1 }
] as const;
const ROW_SHARE = [0.25, 0.25, 0.5];
const PAD = { top: 8, right: 8, bottom: 34, left: 42 };

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/**
 * Uma matriz estratégica do v1: cada bolha é um grupo de menções, `x` a
 * relevância do veículo (0 a 5) e `y` a do conteúdo com o sinal do
 * sentimento (-5 a 5), as duas pela fórmula do v1 calculada no servidor.
 * O tamanho é o volume. As zonas vêm em tons claros da paleta de
 * polaridade (no v1 eram blocos de verde e vermelho fortes, que competiam
 * com as bolhas); a cor da bolha é a da entidade, em ordem fixa, e o
 * nome vai dentro dela — a cor nunca carrega sozinha a identidade.
 */
export function BubbleMatrix({ points, title, showLabel, showCount, showCena, ringOnly = false, toneOf }: {
  points: DashboardMatrixPoint[];
  /** A cor de cada entidade, a MESMA nas duas matrizes: a cor segue a
   *  marca (ou o tema…), nunca a posição dela no ranking de uma fonte. */
  toneOf: (key: string) => string;
  title: string;
  showLabel: boolean;
  showCount: boolean;
  showCena: boolean;
  /** Período: só o contorno, porque os meses se sobrepõem no centro. */
  ringOnly?: boolean;
}) {
  const [frame, width] = useChartWidth<HTMLDivElement>();
  const { bind, tip } = useChartTip(frame);
  if (!points.length) return <EmptyState label="Sem menções nesta fonte"/>;
  const plotWidth = Math.max(160, width - PAD.left - PAD.right);
  const plotHeight = clamp(plotWidth * 0.62, 260, 440);
  const height = PAD.top + plotHeight + PAD.bottom;
  const toX = (value: number) => PAD.left + (clamp(value, 0, 5) / 5) * plotWidth;
  const toY = (value: number) => PAD.top + ((5 - clamp(value, -5, 5)) / 10) * plotHeight;
  const maxCount = Math.max(...points.map(point => point.count), 1);
  // Do maior pro menor: a bolha pequena fica por cima e não some.
  const layered = [...points].sort((a, b) => b.count - a.count);
  let rowTop = PAD.top;
  const rows = ROW_SHARE.map(share => {
    const top = rowTop;
    rowTop += share * plotHeight;
    return { top, height: share * plotHeight };
  });

  return (
    <div className="chart-frame" ref={frame}>
      <svg data-export width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title}>
        {ZONES.map(zone => (
          <g key={zone.key}>
            <rect
              x={PAD.left + zone.col * (plotWidth / 2)}
              y={rows[zone.row].top}
              width={plotWidth / 2}
              height={rows[zone.row].height}
              className={`chart-zone chart-zone--${zone.tone}`}
            />
            <text x={PAD.left + zone.col * (plotWidth / 2) + 10} y={rows[zone.row].top + 16} className="chart-zone-text">{zone.key}</text>
          </g>
        ))}
        <line x1={PAD.left} x2={PAD.left + plotWidth} y1={toY(0)} y2={toY(0)} className="chart-axis-line"/>
        <line x1={toX(2.5)} x2={toX(2.5)} y1={PAD.top} y2={PAD.top + plotHeight} className="chart-axis-line is-soft"/>
        {[5, 0, -5].map(value => (
          <text key={value} x={PAD.left - 6} y={toY(value)} className="chart-axis-text" textAnchor="end" dominantBaseline="middle">{value}</text>
        ))}
        {[0, 2.5, 5].map(value => (
          <text key={value} x={toX(value)} y={PAD.top + plotHeight + 14} className="chart-axis-text" textAnchor="middle">{String(value).replace(".", ",")}</text>
        ))}
        <text x={PAD.left + plotWidth / 2} y={height - 4} className="chart-axis-text" textAnchor="middle">relevância do veículo →</text>
        <text x={12} y={PAD.top + plotHeight / 2} className="chart-axis-text" textAnchor="middle" transform={`rotate(-90 12 ${PAD.top + plotHeight / 2})`}>relevância do conteúdo →</text>
        {layered.map(point => {
          const diameter = ringOnly ? 42 + Math.sqrt(point.count / maxCount) * 46 : 30 + Math.sqrt(point.count / maxCount) * 56;
          const radius = Math.min(diameter / 2, plotHeight / 4);
          const cx = clamp(toX(point.x), PAD.left + radius + 2, PAD.left + plotWidth - radius - 2);
          const cy = clamp(toY(point.y), PAD.top + radius + 2, PAD.top + plotHeight - radius - 2);
          const tone = toneOf(point.key);
          // ~6,5 px por letra na fonte de 11 px: o rótulo cabe no diâmetro.
          const fit = Math.max(3, Math.floor((radius * 2 - 6) / 6.5));
          const lines = [
            showLabel ? (point.label.length > fit ? `${point.label.slice(0, fit - 1)}…` : point.label) : null,
            showCount ? formatInteger(point.count) : null,
            showCena && point.average_cena !== null ? `CENA ${formatCena(point.average_cena)}` : null
          ].filter((line): line is string => line !== null);
          const textClass = ringOnly || tone === "cat-4" ? "chart-bubble-text is-dark" : "chart-bubble-text";
          return (
            <g
              key={point.key}
              tabIndex={0}
              aria-label={`${point.label}: ${formatInteger(point.count)} menções, x ${point.x.toFixed(2)}, y ${point.y.toFixed(2)}`}
              {...bind({
                title: point.label,
                lines: [
                  { label: "Publicações", value: formatInteger(point.count) },
                  { label: "Relevância do veículo", value: point.x.toFixed(2).replace(".", ",") },
                  { label: "Relevância do conteúdo", value: point.y.toFixed(2).replace(".", ",") },
                  { label: "CENA média", value: formatCena(point.average_cena) }
                ]
              })}
            >
              <circle
                cx={cx}
                cy={cy}
                r={radius}
                className={ringOnly ? `chart-bubble is-ring chart-stroke--${tone}` : `chart-bubble chart-tone--${tone}`}
              />
              {radius >= 16 && lines.map((line, lineIndex) => (
                <text
                  key={lineIndex}
                  x={cx}
                  y={cy + (lineIndex - (lines.length - 1) / 2) * 12}
                  className={textClass}
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
      {tip}
    </div>
  );
}

/** A legenda lateral da matriz: cor, nome e, conforme os interruptores,
 *  a quantidade e a CENA média. */
export function BubbleLegend({ points, showCount, showCena, toneOf }: {
  points: DashboardMatrixPoint[]; showCount: boolean; showCena: boolean; toneOf: (key: string) => string;
}) {
  if (!points.length) return null;
  return (
    <ol className="chart-bubble-legend">
      {points.map(point => (
        <li key={point.key}>
          <span className={`chart-legend-swatch chart-tone--${toneOf(point.key)}`} aria-hidden="true"/>
          <span className="chart-bubble-legend-label">{point.label}</span>
          {showCount && <strong>{formatInteger(point.count)}</strong>}
          {showCena && <small>CENA {formatCena(point.average_cena)}</small>}
        </li>
      ))}
    </ol>
  );
}
